import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { IPostListingFilterModel, IPostModel } from '../../../models/post.model';
import {
    ICheckEmailRegisteredAPIResponse,
    IFollowRequestFilterModel,
    IShareUserAPIResponse,
    IUpdateUserProfileModel,
    IUserListingFilterModel,
    IUserModel,
    IUserProfileModel,
} from '../../../models/user.model';
import { BaseApiService } from '../../base-api/base-api.service';

@Injectable({
    providedIn: 'root',
})
export class UserService extends BaseApiService {
    followRequestSubject = new Subject<void>();
    followRequestUpdated$ = this.followRequestSubject.asObservable();

    protected override endPoint = '/users';

    acceptFollowRequest(userId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${userId}/request`, {});
    }

    rejectFollowRequest(userId: number): Observable<void> {
        return this.http.delete<void>(`${this.fullPath}/${userId}/request`, {});
    }

    getFollowRequests(filter: IFollowRequestFilterModel = {}): Observable<{ total: number; result: IUserModel[] }> {
        const params = {
            page: filter.page ?? 1,
            size: filter.size ?? 20,
        };

        return this.http.get<{ total: number; result: IUserModel[] }>(`${this.fullPath}/follows/request`, { params });
    }

    getUsersListing(filter: IUserListingFilterModel): Observable<IUserModel[]> {
        const params = {
            page: filter.page ?? 1,
            size: filter.size ?? 20,
            ...(filter.keyword ? { keyword: filter.keyword } : {}),
            ...(filter.notInGroup ? { notInGroup: filter.notInGroup } : {}),
        };

        return this.http.get<IUserModel[]>(`${this.fullPath}`, { params });
    }

    checkEmailRegistered(email: string): Observable<ICheckEmailRegisteredAPIResponse> {
        return this.http.get<ICheckEmailRegisteredAPIResponse>(`${this.fullPath}/email/verify/${email}`);
    }

    getUserProfile(): Observable<IUserProfileModel> {
        return this.http.get<IUserProfileModel>(`${this.fullPath}/profile`);
    }

    getOtherUserProfile(userId: number): Observable<IUserProfileModel> {
        return this.http.get<IUserProfileModel>(`${this.fullPath}/profile/${userId}`);
    }

    getUserPostsListing(userId: number, filter: IPostListingFilterModel = {}): Observable<IPostModel[]> {
        const params = {
            type: filter.type ?? 'all',
            sort: filter.sort ?? 'latest',
            page: filter.page ?? 1,
            size: filter.size ?? 20,
            ...(filter.cats?.length ? { cats: filter.cats.join(',') } : {}),
        };

        return this.http.get<IPostModel[]>(`${this.fullPath}/${userId}/posts`, { params });
    }

    completeUserProfile(name: string, email: string): Observable<void> {
        const payload = {
            name,
            email,
        };

        return this.http.post<void>(`${this.fullPath}/complete`, payload);
    }

    deleteUserAccount(): Observable<void> {
        return this.http.delete<void>(`${this.fullPath}`);
    }

    generateShareUserLink(userId: number): Observable<IShareUserAPIResponse> {
        return this.http.post<IShareUserAPIResponse>(`${this.fullPath}/${userId}/share`, {});
    }

    updateUserProfile(data: IUpdateUserProfileModel): Observable<void> {
        return this.http.put<void>(`${this.fullPath}/profile`, data);
    }

    blockUser(userId: number): Observable<void> {
        return this.http.post<void>(`${this.fullPath}/${userId}/block`, {});
    }

    unblockUser(userId: number): Observable<void> {
        return this.http.delete<void>(`${this.fullPath}/${userId}/block`);
    }
}
